import {
  NetworkStatus,
  useMutation,
  useQuery,
  useSubscription,
  type ApolloError,
} from '@apollo/client';
import { useEffect, useState, useCallback, useRef } from 'react';

import {
  GetAllChatsQuery,
  GetChatWithMessagesQuery,
  CreateChatMutation,
  OnMessageCreatedSubscription,
  SendMessageMutation,
  Message,
  GetModelsQuery,
  CreateChatMutationVariables,
  GetChatWithMessagesQueryVariables,
  SendMessageMutationVariables,
  ContentItemType,
  OnMessageChunkReceivedSubscription,
} from '../../gql/graphql';
import { graphql } from '../../gql';

interface PlaceholderMessage extends Message {
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

const PLACEHOLDER_PREFIX = 'placeholder-';
const MSG_ERR_MUTATION = 'Oopsie! Could not send message, nya~ T_T';
const MSG_ERR_SUBSCRIPTION = 'Eeep! Error getting response, nya~ >_<';
const MSG_PLACEHOLDER_LOADING = 'Thinking...'; // Optional initial text

// --- Queries and Mutations (Keep these as they are) ---

const READ_CHATS = graphql(/* GraphQL */ `
  query GetAllChats {
    chats {
      id
    }
  }
`);

export const SELECT_CHAT = graphql(/* GraphQL */ `
  query GetChatWithMessages($chatId: Int!) {
    chat(id: $chatId) {
      id
      createdAt
      modelId
      messages {
        id
        content {
          type
          text
        }
        role
        timestamp
      }
    }
  }
`);

const CREATE_CHAT = graphql(/* GraphQL */ `
  mutation CreateChat(
    $prompt: String!
    $modelId: String!
    $provider: String!
    $memberId: String!
  ) {
    createChat(
      input: {
        prompt: $prompt
        local: false
        modelId: $modelId
        provider: $provider
        memberId: $memberId
      }
    ) {
      id
      createdAt
    }
  }
`);

const MESSAGE_SUBSCRIPTION = graphql(/* GraphQL */ `
  subscription OnMessageCreated($chatId: Int!) {
    messageCreated(chatId: $chatId) {
      id
      content {
        type
        text
      }
      role
      timestamp
      streamingId     
    }
  }
`);

const MESSAGE_CHUNK_SUBSCRIPTION = graphql(/* GraphQL */ `
  subscription OnMessageChunkReceived($chatId: Int!) {
    messageChunkReceived(chatId: $chatId) {
      chatId
      textBatch
      streamingId
      role
    }
  }
`);

const SEND_MESSAGE_MUTATION = graphql(/* GraphQL */ `
  mutation SendMessage($chatId: Int!, $prompt: String!) {
    sendMessage(chatId: $chatId, prompt: $prompt) {
      error
      success
      messageId
    }
  }
`);

const DELETE_CHAT = graphql(/* GraphQL */ `
  mutation RemoveChat($id: Int!) {
    removeChat(id: $id)
  }
`);

const GET_MODELS = graphql(/* GraphQL */ `
  query GetModels {
    getModels {
      provider
      modelId
      modelName
    }
  }
`);

// --- Hooks ---

export const useModels = () => {
  const { data } = useQuery<GetModelsQuery>(GET_MODELS);
  return { models: data?.getModels ?? [] };
};

export const useReadChats = () => {
  const { data } = useQuery<GetAllChatsQuery>(READ_CHATS);
  return { chats: data?.chats ?? [] };
};

export const useReadChat = (chatId: number | null | undefined) => {
  const { data, networkStatus, loading, error } = useQuery<
    GetChatWithMessagesQuery,
    GetChatWithMessagesQueryVariables
  >(SELECT_CHAT, {
    variables: { chatId: chatId! },
    skip: !chatId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    chat: data?.chat,
    loading:
      loading || networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.refetch,
    error,
    networkStatus,
  };
};

interface SubscribeChatProps {
  chatId: number | undefined;
  initialMessages: ReadonlyArray<Message> | undefined;
}

export const useSubscribeChat = ({ chatId, initialMessages }: SubscribeChatProps) => {
  const [messages, setMessages] = useState<PlaceholderMessage[]>([]);
  // Ref to track the ID of the temporary placeholder message
  const placeholderIdRef = useRef<string | null>(null);

  useEffect(() => {
    console.log('Setting initial messages, nya~!', initialMessages);
    setMessages(
      initialMessages
        ? [...initialMessages]
            .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
            .map((m) => ({ ...m, isLoading: false, isError: false }))
        : [],
    );
  }, [initialMessages]);

  const updateMessageState = useCallback(
    (id: string | number, updates: Partial<PlaceholderMessage>) => {
      setMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg)));
    },
    [],
  );

  // --- Final Message Subscription ---
  useSubscription<OnMessageCreatedSubscription>(MESSAGE_SUBSCRIPTION, {
    variables: { chatId: chatId! },
    skip: !chatId,
    onError: (error: ApolloError) => {
      console.error('Final message subscription failed, nyaa~!', error);
      // Try to update the placeholder or streaming message to show error
      const streamingId =
        null; /* Extract streamingId if possible from error context or common patterns */
      const idToUpdate = streamingId || placeholderIdRef.current;
      if (idToUpdate) {
        updateMessageState(idToUpdate, {
          isLoading: false,
          isStreaming: false,
          isError: true,
          errorMessage: MSG_ERR_SUBSCRIPTION,
          content: [{ type: ContentItemType.Text, text: MSG_ERR_SUBSCRIPTION }],
        });
        placeholderIdRef.current = null; // Clear placeholder ref
      }
    },
    onData: ({ data }) => {
      const finalMessage = data.data?.messageCreated;
      if (finalMessage) {
        console.log('Final message received via subscription, uwu!', finalMessage);
        setMessages((prev) => {
          const messageIndex = prev.findIndex(
            (msg) =>
              (msg.id as unknown as string) === finalMessage.streamingId ||
              msg.id === placeholderIdRef.current, // Check placeholder too
          );

          const finalMsgData = {
            ...finalMessage,
            id: finalMessage.id,
            timestamp: new Date(finalMessage.timestamp).toISOString(),
            isLoading: false,
            isStreaming: false,
            isError: false,
          } as PlaceholderMessage;

          if (messageIndex > -1) {
            // Replace the streaming/placeholder message with the final one
            const updatedMessages = [...prev];
            updatedMessages[messageIndex] = finalMsgData;
            placeholderIdRef.current = null; // Clear placeholder ref
            return updatedMessages;
          } else {
            console.warn(
              'Final message received but no prior placeholder/streaming message found. Adding directly.',
            );
            // Avoid duplicates if it somehow already exists by final ID
            if (prev.some((msg) => msg.id === finalMessage.id)) return prev;
            return [finalMsgData, ...prev];
          }
        });
        placeholderIdRef.current = null; // Ensure cleared if reached here
      }
    },
  });

  // --- Streaming Chunk Subscription ---
  useSubscription<OnMessageChunkReceivedSubscription>(MESSAGE_CHUNK_SUBSCRIPTION, {
    variables: { chatId: chatId! },
    skip: !chatId,
    onError: (error: ApolloError) => {
      console.error('Chunk subscription failed.', error);
      // Try to update the placeholder or streaming message to show error
      const streamingId = null /* Extract streamingId if possible from error context */;
      const idToUpdate = streamingId || placeholderIdRef.current;
      if (idToUpdate) {
        updateMessageState(idToUpdate, {
          isLoading: false,
          isStreaming: false,
          isError: true,
          errorMessage: MSG_ERR_SUBSCRIPTION,
          content: [{ type: ContentItemType.Text, text: MSG_ERR_SUBSCRIPTION }],
        });
        placeholderIdRef.current = null; // Clear placeholder ref
      }
    },
    onData: ({ data }) => {
      const chunk = data.data?.messageChunkReceived;
      if (chunk) {
        console.log('Chunk received:', chunk);
        setMessages((prev) => {
          const updatedMessages = [...prev]; // Ensure immutability
          const messageIndex = updatedMessages.findIndex(
            (msg) => (msg.id as unknown as string) === chunk.streamingId,
          );

          // --- First Chunk Arrived ---
          if (messageIndex === -1 && placeholderIdRef.current) {
            const placeholderIndex = updatedMessages.findIndex(
              (msg) => msg.id === placeholderIdRef.current,
            );
            if (placeholderIndex > -1) {
              console.log(
                `Replacing placeholder ${placeholderIdRef.current} with first chunk for streamingId ${chunk.streamingId}`,
              );
              // Replace placeholder with the first chunk data
              updatedMessages[placeholderIndex] = {
                // Keep original placeholder fields? Or replace entirely? Replacing seems cleaner.
                id: chunk.streamingId, // Update ID to streamingId!
                chatId: chunk.chatId,
                role: chunk.role as 'assistant',
                content: [{ type: ContentItemType.Text, text: chunk.textBatch }],
                timestamp: Date.now(), // Use chunk timestamp or current? Using current for now.
                isLoading: false, // No longer loading placeholder
                isStreaming: true, // Now streaming!
                isError: false,
              } as PlaceholderMessage;
              placeholderIdRef.current = null; // Clear placeholder ref as it's replaced
              return updatedMessages;
            } else {
              console.warn(
                `Chunk received for ${chunk.streamingId}, but placeholder ${placeholderIdRef.current} not found! Creating new message.`,
              );
              // Fallback: Create new message if placeholder vanished (shouldn't happen ideally)
              placeholderIdRef.current = null; // Clear ref if placeholder is missing
              const newMessage: PlaceholderMessage = {
                id: chunk.streamingId,
                chatId: chunk.chatId,
                role: chunk.role as 'assistant',
                content: [{ type: ContentItemType.Text, text: chunk.textBatch }],
                timestamp: Date.now(),
                isLoading: false,
                isStreaming: true,
                isError: false,
              } as unknown as PlaceholderMessage;
              return [newMessage, ...prev]; // Add to start
            }
          }
          // --- Subsequent Chunk ---
          else if (messageIndex > -1) {
            const targetMessage = { ...updatedMessages[messageIndex] }; // Clone message
            if (targetMessage.content[0]?.type === ContentItemType.Text) {
              const updatedContent = [...targetMessage.content];
              updatedContent[0] = {
                ...updatedContent[0],
                text: (updatedContent[0].text || '') + chunk.textBatch, // Append chunk
              };
              targetMessage.content = updatedContent;
            }
            targetMessage.isStreaming = true; // Ensure streaming status
            targetMessage.isLoading = false; // Ensure loading is false
            targetMessage.isError = false; // Clear any previous error state if streaming resumes
            updatedMessages[messageIndex] = targetMessage; // Update array
            return updatedMessages;
          }
          // --- Chunk arrived but no placeholder or existing message found ---
          else {
            console.warn(
              `Chunk received for ${chunk.streamingId}, but no placeholder ref and no existing message found. Creating new message.`,
            );
            // Fallback: Create new message if needed
            const newMessage: PlaceholderMessage = {
              id: chunk.streamingId,
              chatId: chunk.chatId,
              role: chunk.role as 'assistant',
              content: [{ type: ContentItemType.Text, text: chunk.textBatch }],
              timestamp: Date.now(),
              isLoading: false,
              isStreaming: true,
              isError: false,
            } as unknown as PlaceholderMessage;
            // Avoid adding if a message with this streamingId already exists somehow
            if (prev.some((msg) => msg.id === chunk.streamingId)) return prev;
            return [newMessage, ...prev]; // Add to start
          }
        });
      }
    },
  });

  // --- Send Message Mutation ---
  const [sendMessageMutation, { loading: sending, error: sendError }] = useMutation<
    SendMessageMutation,
    SendMessageMutationVariables
  >(SEND_MESSAGE_MUTATION, {
    onError: (error: ApolloError) => {
      // Error during the mutation call itself
      console.error('Message send mutation failed, baka onii-chan!', error);
      // Update the placeholder message to show the error
      if (placeholderIdRef.current) {
        updateMessageState(placeholderIdRef.current, {
          isLoading: false,
          isStreaming: false,
          isError: true,
          errorMessage: MSG_ERR_MUTATION,
          content: [{ type: ContentItemType.Text, text: MSG_ERR_MUTATION }],
        });
        placeholderIdRef.current = null; // Clear placeholder ref after error
      }
      // Also remove the optimistic user message? Or mark it as failed? For now, leave it.
    },
  });

  // --- sendMessage Function ---
  const sendMessage = useCallback(
    async (input: { chatId: number; prompt: string }) => {
      console.log('Sending message:', input);
      const tempUserMessageId = `user-${Date.now()}`;
      const userMessage: PlaceholderMessage = {
        id: tempUserMessageId as unknown as number, // Cast needed if Message['id'] is number
        chatId: input.chatId,
        role: 'user',
        timestamp: new Date().toISOString(),
        content: [{ type: ContentItemType.Text, text: input.prompt }],
        isLoading: false,
        isError: false,
        isStreaming: false,
      };

      // --- Create Placeholder Assistant Message ---
      const tempPlaceholderId = `${PLACEHOLDER_PREFIX}${Date.now()}`;
      const placeholderAssistantMessage: PlaceholderMessage = {
        id: tempPlaceholderId,
        chatId: input.chatId,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        content: [{ type: ContentItemType.Text, text: MSG_PLACEHOLDER_LOADING }], // Placeholder text
        isLoading: true, // Indicate loading
        isStreaming: false,
        isError: false,
      } as unknown as PlaceholderMessage; // Cast needed if Message type doesn't have flags

      // Add optimistic user message AND placeholder assistant message
      setMessages((prev) => [placeholderAssistantMessage, userMessage, ...prev]);
      placeholderIdRef.current = tempPlaceholderId; // Store placeholder ID

      try {
        const response = await sendMessageMutation({
          variables: input,
        });

        const success = response?.data?.sendMessage?.success;

        if (!success) {
          console.error('Mutation reported failure but did not throw error.');
          // Update placeholder to show error
          if (placeholderIdRef.current) {
            updateMessageState(placeholderIdRef.current, {
              isLoading: false,
              isStreaming: false,
              isError: true,
              errorMessage: MSG_ERR_MUTATION,
              content: [{ type: ContentItemType.Text, text: MSG_ERR_MUTATION }],
            });
            placeholderIdRef.current = null; // Clear placeholder ref
          }
          // Optionally remove optimistic user message or mark as failed
          // setMessages((prev) => prev.filter((m) => m.id !== tempUserMessageId));
        }
        // If successful, the subscriptions will handle updating/replacing the placeholder
      } catch (err) {
        // Error already handled by useMutation's onError, but logged here again if needed
        console.error('Error caught in sendMessage function:', err);
        // The onError handler for the mutation should have updated the placeholder state.
        // Ensure placeholder ref is cleared if an error occurred here too
        if (placeholderIdRef.current) {
          // Maybe call updateMessageState again just in case onError didn't trigger properly
          // updateMessageState(...)
          placeholderIdRef.current = null;
        }
      }
    },
    [sendMessageMutation, chatId, updateMessageState], // Added chatId dependency
  );

  // Combined loading state: either mutation is running or we have a placeholder active
  const isLoading = sending || !!placeholderIdRef.current;

  // Report mutation error, subscription errors are handled inside hooks
  const error = sendError || null;

  return { messages, sendMessage, sending: isLoading, sendError: error };
};

export const useCreateChat = () => {
  const [createChatMutation, { loading, error }] = useMutation<
    CreateChatMutation,
    CreateChatMutationVariables
  >(CREATE_CHAT);

  const createChat = useCallback(
    async (
      prompt: string,
      modelId: string,
      provider: string,
      memberId: string,
    ): Promise<CreateChatMutation['createChat'] | null> => {
      try {
        console.log('Attempting to create chat, ze~!', {
          prompt,
          modelId,
          provider,
          memberId,
        });
        const result = await createChatMutation({
          variables: {
            prompt,
            modelId,
            provider,
            memberId,
          },
          refetchQueries: [{ query: READ_CHATS }],
          // We could also update the cache manually, but refetch is simpler for now, ze~!
          // update(cache, { data }) { ... }
        });
        console.log('Chat creation successful, ze~!', result.data?.createChat);
        return result.data?.createChat ?? null; // Return the created chat data
      } catch (err) {
        console.error('Chat creation failed, ze~!', err);
        return null; // Return null on error
      }
    },
    [createChatMutation],
  );

  return { createChat, loading, error };
};

export const useDeleteChat = () => {
  const [deleteChatMutation, { loading, error }] = useMutation(DELETE_CHAT);

  const deleteChat = useCallback(
    (id: number) => {
      console.log(`Attempting to delete chat ${id}, ze~!`);
      return deleteChatMutation({
        variables: { id },
        refetchQueries: [READ_CHATS],
        // Optimistic update could remove it from the list immediately, ze~!
        // update(cache) { ... }
      });
    },
    [deleteChatMutation],
  );

  return { deleteChat, loading, error };
};
